'use client';

import { PropsWithChildren, createContext, use, useEffect, useState } from 'react';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import { SecuredKey, getAlgoliaSearchKey } from '@/lib/algolia/actions';

const INTERVAL = 60 * 1000; // check every 1 minute to see if the key is expiring soon
const BUFFER = 5 * 60 * 1000; // when the key is expiring in 5 minutes, refresh it
const SearchClientContext = createContext<{
  searchClient: SearchClient;
}>({
  // this is only used if trying to use the context outside the provider, which should not happen
  searchClient: algoliasearch('', ''),
});
export const useSearchClient = () => {
  const { searchClient } = use(SearchClientContext);
  return { searchClient };
};

export const SearchClientProvider = ({
  algoliaSearchKey,
  children,
}: PropsWithChildren<{ algoliaSearchKey: SecuredKey }>): JSX.Element => {
  const { appId, securedKey, validUntil } = algoliaSearchKey;
  const [expiration, setExpiration] = useState<number>(validUntil * 1000);
  const [searchClient, setSearchClient] = useState<SearchClient>(algoliasearch(appId, securedKey));

  useEffect(() => {
    if (!expiration) {
      return;
    }
    const interval = setInterval(async () => {
      const now = Date.now();
      const expiringSoon = expiration - BUFFER - now < 0;
      console.debug('Checking if search key is expiring soon', { expiration, now, expiringSoon });
      if (expiringSoon) {
        console.debug('Search key is expiring soon, refreshing search client');

        const { appId, securedKey, validUntil } = await getAlgoliaSearchKey();
        setSearchClient(algoliasearch(appId, securedKey));
        setExpiration(validUntil * 1000);

        console.debug('New search key expiration', {
          validUntil,
          timeRemaining: (validUntil * 1000 - Date.now()) / 1000,
        });
      }
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [expiration]);

  return (
    <SearchClientContext.Provider value={{ searchClient }}>{children}</SearchClientContext.Provider>
  );
};
